import { encounterService } from "@/services";
import {
  DataGridPro,
  GridColDef,
  GridPaginationModel
} from "@mui/x-data-grid-pro";
import { Box, css } from "@mui/material";
import { ActionsCell, AtsCell, NotesCell, StatusCell } from "@/components/PatientList/PatientListCells";
import { notesToString, tasksToString } from "@/components/PatientList/PatientListColumns";
import { GridRowSelectionModel } from "@mui/x-data-grid";
import { PatientEncounter } from "@/resources/types";
import moment from "moment";
import { Text } from "@imminently/react/ui/core/text";
import { useNavigate } from "react-router-dom";
import { getPatientRoute } from "@/pages";
import { usePagination } from "@imminently/react/store/query";

const { useResources } = encounterService;

const tableStyle = css`
  --border: 1px solid #e5e5e5;
  --border-radius: 1rem;

  border: none;
  height: 400px;
  width: 100%;
  max-width: 100%;
  overflow: hidden;

  .MuiDataGrid-virtualScroller {
    border-left: var(--border);
    border-right: var(--border);
    border-top-left-radius: var(--border-radius);
    border-top-right-radius: var(--border-radius);
  }

  .MuiDataGrid-columnHeaders {
    border: none;

    .MuiDataGrid-columnSeparator svg {
      opacity: 0;
    }
  }

  .MuiDataGrid-footerContainer {
    border: var(--border);
    border-bottom-left-radius: var(--border-radius);
    border-bottom-right-radius: var(--border-radius);
  }

  .MuiDataGrid-virtualScroller {
    border-top: var(--border);
    border-top-left-radius: var(--border-radius);
    border-top-right-radius: var(--border-radius);
  }

  .location-row--even {
    background-color: #FFF;
  }

  .location-row--odd {
    background-color: #F0F3FF;
  }

  .MuiDataGrid-cell {
    display: flex;
    align-items: center;
  }
`;

const CreatedCell = ({ row }: { row: PatientEncounter }) => {
  if (!row.created) return null;
  const tooltip = moment(row.created).format("DD/MM/YYYY [at] h:mm a");

  return (
    <Text>{tooltip}</Text>
  );
};

const usePaginationModel = ({
  setPagination,
  pagination,
}: ReturnType<typeof usePagination>) => {
  const onPageChange = (model: GridPaginationModel) => {
    setPagination({ page: model.page, perPage: model.pageSize });
  };

  const paginationModel = {
    page: pagination.page,
    pageSize: pagination.perPage,
  };

  return { paginationModel, onPageChange };
};
const PatientEncounters = ({ encounter }: any) => {
  const navigate = useNavigate();

  const {
    data: encounters,
    total,
    query,
    pagination,
  } = useResources({
    filter: {
      patient: encounter.patient.id,
      created: `$ne=${encounter.created}` // HACK: THe backend has issues when you query with id= (as it thinks its a GET then). So we use created as that should be relatively unique
    },
  } as any);

  const { paginationModel, onPageChange } = usePaginationModel(pagination as any);

  if (!encounters) return null;
  /*const paginationModel = {
    page: pagination.pagination.page,
    pageSize: pagination.pagination.perPage,
  };*/
  const columns = [{
    field: "created",
    headerName: "Created",
    minWidth: 200,
    width: 200,
    renderCell: CreatedCell
  },{
    field: "status",
    headerName: "Status",
    minWidth: 100,
    width: 100,
    renderCell: StatusCell
  }, {
    field: "atsScore",
    headerName: "ATS",
    minWidth: 50,
    width: 50,
    renderCell: AtsCell,
  },{
    field: "discharged",
    headerName: "Discharged",
    minWidth: 120,
    width: 120,
  },{
    field: "notes",
    headerName: "Notes",
    sortable: false,
    minWidth: 200,
    width: 250,
    valueFormatter: (value) => notesToString(value),
    renderCell: NotesCell,
  }, {
    field: "tasks",
    headerName: "Tasks",
    sortable: false,
    minWidth: 170,
    width: 210,
    valueGetter: (_: any, row: any) =>
      row as PatientEncounter,
    valueFormatter: (_: any, row: any) =>
      tasksToString(row as PatientEncounter),
    renderCell: ActionsCell,
  }] as GridColDef[];


  const onSelect = (model: GridRowSelectionModel) => {
    if (model.length > 0) {
      const rowId = model[0] as string;
      const id = encounters.find((r) => r.id === rowId)?.id;
      if (id) {
        navigate(getPatientRoute(id));
      }
    }
  };
  const pageSizeOptions = [20, 50, 100];

  return (
    <Box sx={{ width: "100%", overflow: "hidden" }}>
      <i>Displays the patient&apos;s visits that were recorded in this system. Please note that it might not include visits recorded in other systems.</i>
      { encounters && (
        <DataGridPro
          css={tableStyle}
          rows={encounters}
          rowCount={total}
          columns={columns}
          loading={query.isLoading}
          pagination
          paginationMode="server"
          disableColumnMenu
          onRowSelectionModelChange={onSelect}
          paginationModel={paginationModel}
          pageSizeOptions={pageSizeOptions}
          onPaginationModelChange={onPageChange}
          {...({} as any)}
        />
      )}
    </Box>
  );
};
export default PatientEncounters;